import { UploadCloud, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UploadProgress } from "@/components/project/UploadProgress";

export const SAMPLE = `SECTION 05 12 00 - STRUCTURAL STEEL FRAMING

PART 1 - GENERAL
1.1 SUMMARY
   A. Section includes structural steel framing per AISC 360 and AISC 303.

1.4 SUBMITTALS
   A. Shop Drawings: Fabricator shall prepare and stamp all connection designs per AISC Code of Standard Practice Section 3.1.
   B. Fabricator shall pay for all third-party CWI field inspections and NDT.

2.3 MATERIALS
   A. Wide-flange shapes: ASTM A992, Grade 50.
   B. HSS: ASTM A500 Grade C.
   C. All AESS Level 3 members per AISC 303 Section 10 shall receive continuous ground welds with a 250 grit finish.

3.2 SURFACE PREPARATION
   A. Shop primer: SSPC-PC 15.05 zinc-rich primer, 3 mils DFT.
   B. Intumescent fire-proofing shall be applied over shop primer. (NOTE: Contractor shall verify compatibility.)

SECTION 00 72 00 - GENERAL CONDITIONS
   Retainage: 10% withheld until final completion of ALL trades.
   Liquidated damages: $25,000 per calendar day.
   Indemnification: Fabricator shall indemnify Owner and Contractor from any and all claims, including sole negligence of Owner.
`;
export const EMPTY_UPLOAD = { name: "", doc_type: "specification", csi_division: "", content: SAMPLE, file: null };
const input = "mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 focus:outline-none focus:border-amber-500";

export function DeleteConfirmDialog({ confirm, setConfirm, doDelete }) {
  return (
    <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-red-400">Confirm delete</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-300" data-testid="delete-confirm-text">You are about to permanently delete {confirm?.label}. This cannot be undone.</p>
        <DialogFooter>
          <button onClick={() => setConfirm(null)} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200" data-testid="delete-cancel-button">Cancel</button>
          <button onClick={doDelete} className="bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-2 rounded-md text-sm" data-testid="delete-confirm-button">Delete</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ModeButton = ({ active, onClick, testId, children }) => (
  <button onClick={onClick} className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase ${active ? "bg-amber-500 text-slate-950 font-bold" : "border border-slate-700 text-slate-400"}`} data-testid={testId}>{children}</button>
);

export function UploadDialog({ open, setOpen, form, setForm, uploadMode, setUploadMode, uploading, submit, up, upDoc, resetUpload, limits, onVerify }) {
  const transferring = up?.phase === "transfer" || up?.phase === "finalizing";
  const showForm = !up || up.phase === "error";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && transferring) return; if (!o) resetUpload(); setOpen(o); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl" onEscapeKeyDown={(e) => transferring && e.preventDefault()} onPointerDownOutside={(e) => transferring && e.preventDefault()}>
        <DialogHeader><DialogTitle className="font-heading">{up && !showForm ? "Document upload" : "Upload Document"}</DialogTitle><DialogDescription className="sr-only">Upload a PDF or text document; progress and processing stages are announced here.</DialogDescription></DialogHeader>
        <UploadProgress up={up} doc={upDoc} onRetry={submit} onVerify={onVerify} onClose={() => { resetUpload(); setOpen(false); }} />
        {showForm && (<>
        {limits && <div className="text-[11px] text-slate-500 mb-1" data-testid="upload-limit-note">PDFs up to {limits.max_mb} MB · sent in {Math.round(limits.chunk_bytes / 1048576)} MB parts so large spec books and drawing sets upload reliably</div>}
        <div className="flex gap-2 mb-2">
          <ModeButton active={uploadMode === "file"} onClick={() => setUploadMode("file")} testId="upload-mode-file">PDF / File</ModeButton>
          <ModeButton active={uploadMode === "paste"} onClick={() => setUploadMode("paste")} testId="upload-mode-paste">Paste text</ModeButton>
        </div>
        <label className="block"><span className="label-cap">Name (optional)</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Structural Steel Spec Section 05 12 00" className={input} data-testid="upload-doc-name-input" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label><span className="label-cap">Type</span>
            <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100" data-testid="upload-doc-type-select">
              <option value="contract">Contract</option><option value="specification">Specification</option><option value="addendum">Addendum / Bulletin / Change document</option><option value="drawing">Structural Drawings (sheet set)</option><option value="other">Other</option>
            </select></label>
          <label><span className="label-cap">CSI Division (optional)</span>
            <input value={form.csi_division} onChange={(e) => setForm({ ...form, csi_division: e.target.value })} placeholder="Auto-detect" className={input} /></label>
        </div>
        {uploadMode === "file" ? (
          <div><span className="label-cap">File (PDF or .txt)</span>
            <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-slate-700 hover:border-amber-500/60 rounded-md p-8 cursor-pointer transition" data-testid="upload-file-dropzone">
              <UploadCloud className="w-8 h-8 text-slate-500 mb-2" />
              <div className="text-sm text-slate-300">{form.file ? <span className="text-amber-400 font-mono">{form.file.name}</span> : "Click to select a PDF or text file"}</div>
              <div className="text-xs text-slate-500 mt-1">Text will be extracted automatically.</div>
              <input type="file" accept=".pdf,.txt,.md,text/*,application/pdf" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="upload-file-input" />
            </label></div>
        ) : (
          <label className="block"><span className="label-cap">Content (paste text)</span>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} className={`${input} font-mono text-xs`} data-testid="upload-doc-content-input" /></label>
        )}
        <DialogFooter>
          <button onClick={() => { resetUpload(); setOpen(false); }} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} disabled={uploading} aria-busy={uploading} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-50" data-testid="upload-doc-submit">{uploading ? "Uploading…" : "Upload"}</button>
        </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

const ROLE_CLASS = { admin: "role-admin", reviewer: "role-reviewer", readonly: "role-readonly" };

export function PermissionsDialog({ open, setOpen, projectName, members, projPerms, permForm, setPermForm, setPerm, rmPerm }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">Project Permissions — {projectName}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Per-project overrides. A reviewer can be full-access on Project A but read-only here. Members not listed inherit their workspace role.</p>
        <div className="border border-slate-800 rounded-md p-3">
          <div className="label-cap mb-2">Add override</div>
          <div className="flex gap-2 min-w-0">
            <select value={permForm.user_id} onChange={(e) => setPermForm({ ...permForm, user_id: e.target.value })} className="flex-1 min-w-0 bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100 truncate" data-testid="perm-user-select">
              <option value="">— Select member —</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
            </select>
            <select value={permForm.role} onChange={(e) => setPermForm({ ...permForm, role: e.target.value })} className="shrink-0 bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" data-testid="perm-role-select">
              <option value="admin">Admin</option><option value="reviewer">Reviewer</option><option value="readonly">Read-Only</option>
            </select>
            <button onClick={setPerm} className="shrink-0 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-3 py-2 rounded-md text-sm" data-testid="perm-add-button">Set</button>
          </div>
        </div>
        <div>
          <div className="label-cap mb-2">Active overrides</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {projPerms.map((pp) => (
              <div key={pp.permission_id} className="flex items-center justify-between gap-2 border border-slate-800 rounded-md px-3 py-2 text-sm min-w-0" data-testid={`perm-row-${pp.permission_id}`}>
                <div className="min-w-0 flex-1"><div className="text-slate-100 font-semibold truncate">{pp.name}</div><div className="text-xs text-slate-500 font-mono truncate">{pp.email}</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase font-bold ${ROLE_CLASS[pp.role]}`}>{pp.role}</span>
                  <button onClick={() => rmPerm(pp.permission_id)} className="text-red-400 hover:text-red-300" data-testid={`perm-remove-${pp.permission_id}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {!projPerms.length && <div className="text-sm text-slate-500">No overrides. All members use workspace role.</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
